import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const COR = { online: "bg-emerald-500", offline: "bg-red-500", instavel: "bg-amber-400" };
const ROT = { online: "Online", offline: "Offline", instavel: "Instável" };

const NI = <span className="italic text-slate-400">Não informado</span>;
const MAX_IDADE_MS = 15 * 60 * 1000;

// Hora do coletor = última leitura + tempo decorrido; leitura velha = "Sem atualização"
const horaColetor = (c) => {
  const lido = c.relogio_lido_at ? Date.parse(c.relogio_lido_at) : null;
  if (!c.relogio_time || !lido || Date.now() - lido > MAX_IDADE_MS || c.status === "offline") return null;
  const [d, h] = c.relogio_time.replace("T", " ").split(" ");
  const [Y, M, D] = d.split("-").map(Number);
  const [hh, mm, ss] = h.split(":").map(Number);
  const t = new Date(Date.UTC(Y, M - 1, D, hh, mm, ss) + (Date.now() - lido));
  const p = (n) => String(n).padStart(2, "0");
  return { data: `${p(t.getUTCDate())}/${p(t.getUTCMonth() + 1)}/${t.getUTCFullYear()}`, hora: `${p(t.getUTCHours())}:${p(t.getUTCMinutes())}:${p(t.getUTCSeconds())}` };
};

const Campo = ({ l, v, tid }) => (
  <div className="flex min-w-0 gap-1" data-testid={tid}><span className="shrink-0 text-slate-400">{l}</span><span className="truncate font-medium text-slate-700">{v ?? NI}</span></div>
);

const Coletor = ({ c }) => {
  const h = horaColetor(c);
  const sem = <span className="italic text-amber-600">Sem atualização</span>;
  return (
    <div className="rounded border border-slate-200 px-1.5 py-1 text-[10px] leading-tight" data-testid={`online-coletor-${c.sn}`}>
      <div className="flex items-center gap-1 font-semibold text-slate-800">
        <span className={`h-2 w-2 shrink-0 rounded-full ${COR[c.status]}`} /><span className="truncate">{c.nome || c.sn}</span>
        <span className="ml-auto shrink-0 font-normal text-slate-500">{ROT[c.status]}{c.reconexoes_1h ? ` · ${c.reconexoes_1h} recon./1h` : ""}</span>
      </div>
      <div className="mt-0.5 grid grid-cols-2 gap-x-2">
        <Campo l="SN" v={c.sn || null} tid="col-sn" /><Campo l="Porta" v={c.porta} tid="col-porta" />
        <Campo l="FW" v={c.firmware} tid="col-fw" /><Campo l="Conexão" v={c.conexao === "wifi" ? "Wi-Fi" : c.conexao === "cabo" ? "Cabo de rede" : null} tid="col-conexao" />
        <Campo l="IP" v={c.ip} tid="col-ip" /><Campo l="DHCP" v={c.dhcp === true ? "Ativado" : c.dhcp === false ? "Desativado" : null} tid="col-dhcp" />
        <Campo l="Data" v={h ? h.data : sem} tid="col-data" /><Campo l="Hora" v={h ? h.hora : sem} tid="col-hora" />
      </div>
    </div>
  );
};

const Empresa = ({ g }) => {
  const on = g.coletores.filter((c) => c.status === "online").length;
  return (
    <div className="border-b px-3 py-2" data-testid={`online-empresa-${g.empresa_id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="truncate text-xs font-bold text-slate-800" title={g.empresa}>{g.empresa}</div>
        <div className="shrink-0 text-[10px] text-slate-500">Coletores {on}/{g.coletores.length} online</div>
      </div>
      {g.usuarios.map((u) => (
        <div key={u.email} className="flex items-center gap-1.5 text-xs text-slate-700" data-testid="online-usuario">
          <span className="h-2 w-2 rounded-full bg-emerald-500" /><span className="truncate">{u.nome}</span>
          <span className="ml-auto text-[10px] text-slate-400">Online{u.abas > 1 ? ` · ${u.abas} abas` : ""}</span>
        </div>
      ))}
      {g.coletores.length > 0 && <div className="mt-1 space-y-1">{g.coletores.map((c) => <Coletor key={c.sn} c={c} />)}</div>}
    </div>
  );
};

export const OnlineBadge = () => {
  const [d, setD] = useState({ total: 0, empresas: [] });
  const [aberto, setAberto] = useState(false);
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!aberto) return undefined;
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => { clearInterval(t); };
  }, [aberto]);
  useEffect(() => {
    const load = () => { if (!document.hidden) api.get("/superadmin/online").then((r) => setD(r.data)).catch(() => {}); };
    load();
    const t = setInterval(load, aberto ? 10000 : 20000);
    return () => { clearInterval(t); };
  }, [aberto]);
  return (
    <Popover onOpenChange={setAberto}>
      <PopoverTrigger asChild>
        <button data-testid="btn-online" className="ml-2 inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700 hover:bg-emerald-100">
          <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" /><span data-testid="online-total">{d.total}</span> ONLINE
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-96 p-0" data-testid="online-painel">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-bold">Usuários online agora</span>
          <span className="text-[10px] text-slate-400">atualiza a cada 10s</span>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {d.empresas.length ? d.empresas.map((g) => <Empresa key={g.empresa_id} g={g} />)
            : <div className="px-3 py-4 text-xs text-slate-500" data-testid="online-vazio">Nenhum cliente conectado neste momento.</div>}
        </div>
        <div className="flex gap-3 border-t px-3 py-1.5 text-[10px] text-slate-500">
          <span><span className="mr-1 inline-block h-2 w-2 rounded-full bg-emerald-500" />Online</span>
          <span><span className="mr-1 inline-block h-2 w-2 rounded-full bg-amber-400" />Instável</span>
          <span><span className="mr-1 inline-block h-2 w-2 rounded-full bg-red-500" />Offline</span>
        </div>
      </PopoverContent>
    </Popover>
  );
};
